import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Network, Calendar, FileText, Users, MapPin, Plane,
  RefreshCw, AlertTriangle, FilePlus2, BookOpen, Search, LayoutGrid, List,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  epsteinGraph, epsteinStats, epsteinTimeline, epsteinEntity, epsteinEvidenceList,
} from "../lib/api";
import { EvidenceBoard } from "../components/epstein/EvidenceBoard";
import { DetailPanel } from "../components/epstein/DetailPanel";
import { SubmitForm } from "../components/epstein/SubmitForm";
import { HowToReadModal } from "../components/epstein/HowToReadModal";
import { InlineDetailPanel } from "../components/epstein/InlineDetailPanel";
import { MobileBottomSheet } from "../components/epstein/MobileBottomSheet";
import { EntityList } from "../components/epstein/EntityList";
import {
  FINANCIAL_REL_TYPES, BOARD_FILTERS, DIRECTORY_CATEGORIES,
  EF_DISCLAIMER, CONFIDENCE_META, EVIDENCE_GROUPS, KIND_TO_DIRECTORY_BUCKET,
} from "../components/epstein/efConsts";

const TABS = [
  { key: "board",    label: "Evidence Board", icon: Network,  testid: "ef-tab-board" },
  { key: "timeline", label: "Timeline",       icon: Calendar, testid: "ef-tab-timeline" },
  { key: "documents",label: "Documents",      icon: FileText, testid: "ef-tab-documents" },
  { key: "subjects", label: "Key Subjects",   icon: Users,    testid: "ef-tab-subjects" },
  { key: "map",      label: "Map View",       icon: MapPin,   testid: "ef-tab-map" },
];

// ---- Hero stat grid ----
const StatCell = ({ label, value, accent = "#D92D20", testid }) => (
  <div
    className="border border-[#2D2A26]/80 bg-[#0a0907]/85 backdrop-blur-[2px] px-3 py-3"
    data-testid={testid}
  >
    <div className="font-mono text-[8px] uppercase tracking-[0.3em]" style={{ color: accent }}>
      {label}
    </div>
    <div className="font-display text-2xl text-[#E4DFD3] leading-tight mt-0.5 truncate">
      {value ?? "—"}
    </div>
  </div>
);

const EpsteinFiles = () => {
  const [tab, setTab] = useState("board");
  const [boardMode, setBoardMode] = useState("board"); // board | list (used by board + subjects)
  const [stats, setStats] = useState(null);
  const [graph, setGraph] = useState({ nodes: [], edges: [], anchor: null, anchor_image_url: null });
  const [timeline, setTimeline] = useState([]);
  const [evidenceList, setEvidenceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boardFilter, setBoardFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showSubmit, setShowSubmit] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [inlineEntity, setInlineEntity] = useState(null);
  const [inlineSources, setInlineSources] = useState([]);
  const [inlineLoading, setInlineLoading] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  // When the user presses Escape (desktop) we fully dismiss the drawer.
  // The default-anchor effect would otherwise re-hydrate immediately, so we
  // gate it with a one-shot flag that clears the next time the user clicks
  // a card.
  const [drawerDismissed, setDrawerDismissed] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024,
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // First-visit onboarding
  useEffect(() => {
    try {
      if (!localStorage.getItem("ef_howto_seen_v1")) setShowHowTo(true);
    } catch { /* noop */ }
  }, []);
  const closeHowTo = useCallback(() => {
    setShowHowTo(false);
    try { localStorage.setItem("ef_howto_seen_v1", String(Date.now())); } catch { /* noop */ }
  }, []);

  // ---- Data load ----
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, s] = await Promise.all([epsteinGraph("board"), epsteinStats()]);
      setGraph(g); setStats(s);
      const [tl, ev] = await Promise.all([epsteinTimeline(), epsteinEvidenceList()]);
      setTimeline(tl.events || []);
      setEvidenceList(ev.items || []);
    } catch {
      toast.error("Failed to load The Epstein Files.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Default inline panel = anchor entity once graph loads (unless the user
  // has explicitly dismissed the drawer via Escape).
  useEffect(() => {
    if (!drawerDismissed && !inlineEntity && graph.anchor && graph.nodes.length) {
      const anchorNode = graph.nodes.find((n) => n.id === graph.anchor.id) || graph.anchor;
      setInlineEntity(anchorNode);
    }
  }, [graph, inlineEntity, drawerDismissed]);

  // Lazy-load full sources for the inline-selected entity
  useEffect(() => {
    if (!inlineEntity?.slug) { setInlineSources([]); return; }
    let alive = true;
    setInlineLoading(true);
    epsteinEntity(inlineEntity.slug)
      .then((d) => { if (alive) setInlineSources(d.sources || []); })
      .catch(() => { if (alive) setInlineSources([]); })
      .finally(() => { if (alive) setInlineLoading(false); });
    return () => { alive = false; };
  }, [inlineEntity?.slug]);

  // ---- Filter graph by category + search ----
  const filteredGraph = useMemo(() => {
    let edges = graph.edges;
    if (boardFilter !== "all") {
      const f = BOARD_FILTERS.find((x) => x.key === boardFilter);
      if (f) edges = edges.filter(f.test);
    }
    const term = search.trim().toLowerCase();
    let nodes = graph.nodes;
    if (term) {
      nodes = nodes.filter(
        (n) =>
          (n.name || "").toLowerCase().includes(term) ||
          (n.category_label || "").toLowerCase().includes(term) ||
          (n.kind || "").toLowerCase().includes(term) ||
          (n.connection_summary_to_epstein || "").toLowerCase().includes(term),
      );
    }
    // If filtering by category-edges, keep nodes that are touched by remaining edges + anchor
    if (boardFilter !== "all") {
      const touched = new Set();
      edges.forEach((e) => { touched.add(e.src_entity_id); touched.add(e.dst_entity_id); });
      if (graph.anchor?.id) touched.add(graph.anchor.id);
      nodes = nodes.filter((n) => touched.has(n.id));
    }
    return { ...graph, nodes, edges };
  }, [graph, boardFilter, search]);

  // ---- Selection handlers ----
  const handleSelectEntity = (e) => {
    // Inline panel always tracks selection; on mobile we also open the bottom sheet.
    setSelectedEdge(null);
    setInlineEntity(e);
    setDrawerDismissed(false); // any explicit click re-opens the drawer
    if (!isDesktop) setMobileSheetOpen(true);
  };
  const handleSelectEdge = (r) => { setSelectedEntity(null); setSelectedEdge(r); };
  const closeDetail = () => { setSelectedEntity(null); setSelectedEdge(null); };
  const closeMobileSheet = () => setMobileSheetOpen(false);

  // Background tap on the board → clear the active selection. On desktop the
  // drawer falls back to the anchor (board un-dims); on mobile the bottom
  // sheet closes entirely.
  const clearInlineSelection = useCallback(() => {
    if (isDesktop) {
      // Revert to the anchor (or null if no anchor yet) — the default-anchor
      // effect will re-hydrate next render if we set null.
      setInlineEntity(graph.anchor
        ? (graph.nodes.find((n) => n.id === graph.anchor.id) || graph.anchor)
        : null);
    } else {
      setInlineEntity(null);
      setMobileSheetOpen(false);
    }
  }, [isDesktop, graph]);

  // Explicit drawer dismissal — only the × button and Escape key trigger this.
  // Fully closes the drawer and suppresses auto-rehydrate until the user
  // clicks a card again.
  const dismissDrawer = useCallback(() => {
    setInlineEntity(null);
    setMobileSheetOpen(false);
    setDrawerDismissed(true);
  }, []);

  // Escape key — desktop only; fully dismiss the drawer.
  useEffect(() => {
    if (!isDesktop || !inlineEntity) return undefined;
    const onKey = (e) => { if (e.key === "Escape") dismissDrawer(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDesktop, inlineEntity, dismissDrawer]);

  // ---- Lightweight panels ----
  const lastUpdated = stats?.last_updated
    ? new Date(stats.last_updated).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })
    : null;

  return (
    <div className="min-h-screen bg-[#0F0F0F]" data-testid="epstein-files-page">
      {/* ===================== HERO ===================== */}
      <section
        className="relative border-b border-[#2D2A26] overflow-hidden"
        data-testid="ef-hero"
      >
        <img
          src="/epstein-hero/gemini_a.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none"
          data-testid="ef-hero-image"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, rgba(15,15,15,0.95) 0%, rgba(15,15,15,0.85) 38%, rgba(15,15,15,0.55) 70%, rgba(15,15,15,0.55) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(15,15,15,0.45) 0%, rgba(15,15,15,0.0) 30%, rgba(15,15,15,0.65) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)",
            backgroundSize: "3px 3px",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-10 md:py-14">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start">
            {/* Left: title + badges + disclaimer */}
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span
                  className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] border border-[#D92D20] px-2 py-0.5"
                  data-testid="ef-badge-living"
                >
                  // LIVING FILE
                </span>
                <span
                  className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/40 px-2 py-0.5"
                  data-testid="ef-badge-review"
                >
                  // UNDER CONTINUING REVIEW
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5DD4B3] border border-[#5DD4B3]/40 px-2 py-0.5">
                  // EVIDENCE MAP
                </span>
              </div>
              <h1
                className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#E4DFD3] leading-[1.05] mb-3"
                style={{ textShadow: "0 2px 24px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)" }}
                data-testid="ef-title"
              >
                The Epstein Files
              </h1>
              <p
                className="max-w-2xl text-sm sm:text-base text-[#A39E93] leading-relaxed mb-3"
                style={{ textShadow: "0 1px 6px rgba(0,0,0,0.85)" }}
              >
                A living evidence file tracing documented relationships, records, travel logs,
                court filings, reporting, and credible leads connected to Jeffrey Epstein.
              </p>
              <p
                className="max-w-2xl text-xs sm:text-sm italic text-[#CBB892] border-l-2 border-[#D92D20] pl-3"
                data-testid="ef-hero-disclaimer"
              >
                This is an evidence map, not a rumor board. Inclusion does not imply guilt or
                criminal conduct. Public connections are only shown when supported by approved
                documents, court records, credible reporting, or clearly labeled witness claims.
              </p>
            </div>

            {/* Right: stat grid */}
            <div className="grid grid-cols-2 gap-2" data-testid="ef-stat-panel">
              <StatCell label="ENTITIES"        value={stats?.entities ?? 0}        testid="ef-stat-entities" />
              <StatCell label="CONNECTIONS"     value={stats?.relationships ?? 0}   testid="ef-stat-connections" />
              <StatCell label="SOURCES"         value={stats?.sources ?? 0}         testid="ef-stat-sources" />
              <StatCell label="DOCUMENTS"       value={evidenceList.length}         testid="ef-stat-documents" />
              <StatCell label="TIMELINE EVENTS" value={timeline.length}             testid="ef-stat-timeline" />
              <StatCell label="LAST UPDATED"    value={lastUpdated || "—"}          accent="#E0A526" testid="ef-stat-updated" />
              <div className="col-span-2 border border-[#D92D20]/40 bg-[#1a0808]/60 backdrop-blur-[2px] px-3 py-2.5" data-testid="ef-stat-anchor">
                <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#D92D20]">
                  // ANCHOR SUBJECT
                </div>
                <div className="font-display text-lg text-[#E4DFD3] leading-tight mt-0.5">
                  Jeffrey Epstein
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== TAB BAR ===================== */}
      <section className="border-b border-[#2D2A26] bg-[#0a0907] sticky top-0 z-30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
          <div className="flex overflow-x-auto whitespace-nowrap gap-1 -mx-1 px-1" data-testid="ef-tabs">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={t.testid}
                  className={
                    "flex items-center gap-2 px-3 sm:px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 transition-colors " +
                    (active
                      ? "border-[#D92D20] text-[#D92D20]"
                      : "border-transparent text-[#A39E93] hover:text-[#E4DFD3] hover:border-[#454038]")
                  }
                >
                  <t.icon size={12} /> {t.label}
                </button>
              );
            })}
            {/* Flight Logs — first-class evidence-lane section, opens its own route */}
            <Link
              to="/epstein-files/flight-logs"
              data-testid="ef-tab-flight-logs"
              className="flex items-center gap-2 px-3 sm:px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 border-transparent text-[#A39E93] hover:text-[#E4DFD3] hover:border-[#454038] shrink-0 transition-colors"
            >
              <Plane size={12} /> Flight Logs
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== MAIN ===================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-5 md:py-6">
        {/* Control row — board + subjects use the same toggle/filter/search */}
        {(tab === "board" || tab === "subjects") && (
          <ControlRow
            mode={boardMode}
            onMode={setBoardMode}
            filter={boardFilter}
            onFilter={setBoardFilter}
            search={search}
            onSearch={setSearch}
          />
        )}

        {/* Empty state */}
        {!loading && tab === "board" && graph.nodes.length === 0 && (
          <div className="border border-dashed border-[#2D2A26] p-12 text-center" data-testid="ef-empty">
            <AlertTriangle className="text-[#E0A526] mx-auto mb-3" size={28} />
            <div className="font-heading text-xl text-[#E4DFD3] mb-2">No approved evidence on file yet</div>
            <p className="text-[#A39E93] text-sm">
              Public connections only appear once they have been reviewed, source-attached, and
              approved by an editor.
            </p>
          </div>
        )}

        {/* ---- BOARD TAB ---- */}
        {tab === "board" && graph.nodes.length > 0 && (
          <div className="grid lg:grid-cols-[1fr_360px] gap-3 lg:gap-4">
            <div className="min-w-0">
              {boardMode === "board" ? (
                <EvidenceBoard
                  nodes={filteredGraph.nodes}
                  edges={filteredGraph.edges}
                  anchorId={filteredGraph.anchor?.id}
                  anchorImage={filteredGraph.anchor_image_url}
                  selectedEntityId={inlineEntity?.id}
                  onSelectEntity={handleSelectEntity}
                  onSelectEdge={handleSelectEdge}
                  onClearSelection={clearInlineSelection}
                  height={isDesktop ? 760 : 560}
                />
              ) : (
                <EntityList
                  nodes={filteredGraph.nodes}
                  edges={filteredGraph.edges}
                  anchorId={filteredGraph.anchor?.id}
                  selectedId={inlineEntity?.id}
                  onSelectEntity={handleSelectEntity}
                />
              )}
              {/* Page-level legend removed — the Evidence Board now renders
                  its own canonical legend bar at the bottom of the board
                  surface (matches the case-board reference). The page-level
                  Legend component is kept for any future surface that needs it. */}
            </div>
            {isDesktop && (
              <div className="h-[820px] lg:sticky lg:top-[60px]">
                <InlineDetailPanel
                  entity={inlineEntity}
                  allNodes={graph.nodes}
                  edges={graph.edges}
                  sources={inlineSources}
                  onClear={dismissDrawer}
                />
              </div>
            )}
          </div>
        )}

        {/* ---- KEY SUBJECTS TAB ---- */}
        {tab === "subjects" && graph.nodes.length > 0 && (
          <div className="grid lg:grid-cols-[1fr_360px] gap-3 lg:gap-4">
            <div className="min-w-0">
              <DirectoryByCategory
                nodes={filteredGraph.nodes}
                edges={filteredGraph.edges}
                anchorId={filteredGraph.anchor?.id}
                selectedId={inlineEntity?.id}
                onSelectEntity={handleSelectEntity}
              />
            </div>
            {isDesktop && (
              <div className="h-[820px] lg:sticky lg:top-[60px]">
                <InlineDetailPanel
                  entity={inlineEntity}
                  allNodes={graph.nodes}
                  edges={graph.edges}
                  sources={inlineSources}
                  onClear={dismissDrawer}
                />
              </div>
            )}
          </div>
        )}

        {/* ---- TIMELINE TAB ---- */}
        {tab === "timeline" && <TimelineView events={timeline} loading={loading} />}

        {/* ---- DOCUMENTS TAB ---- */}
        {tab === "documents" && (
          <DocumentsView items={evidenceList} loading={loading} />
        )}

        {/* ---- MAP TAB ---- */}
        {tab === "map" && (
          <div className="border border-dashed border-[#2D2A26] bg-[#0a0907] p-10 text-center" data-testid="ef-map-empty">
            <MapPin className="text-[#E0A526] mx-auto mb-3" size={28} />
            <div className="font-heading text-xl text-[#E4DFD3] mb-2">Geographic Map View</div>
            <p className="text-[#A39E93] text-sm max-w-lg mx-auto">
              The geographic map plots properties, flight endpoints, court venues, and
              correspondence routes on an interactive world map. It activates once geo
              coordinates have been imported for the current batch.
            </p>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5a544a] mt-4">
              // COMING SOON
            </div>
          </div>
        )}
      </section>

      {/* ===================== FOOTER WARNING STRIP ===================== */}
      <section className="border-t border-[#2D2A26] bg-[#0a0907]" data-testid="ef-footer-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-4 md:py-5 grid lg:grid-cols-[1fr_auto] gap-4 items-center">
          <p className="text-[12px] italic text-[#A39E93] leading-relaxed border-l-2 border-[#D92D20] pl-3">
            {EF_DISCLAIMER}
          </p>
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <button
              onClick={() => setShowHowTo(true)}
              data-testid="ef-footer-guide"
              className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] hover:text-[#E4DFD3] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              <BookOpen size={12} /> Guide
            </button>
            <button
              onClick={() => load()}
              data-testid="ef-footer-refresh"
              className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] hover:text-[#E4DFD3] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={() => setShowSubmit(true)}
              data-testid="ef-footer-submit"
              className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              <FilePlus2 size={12} /> Submit Tip
            </button>
          </div>
        </div>
      </section>

      {/* Modals — kept for tablet/mobile + edge details */}
      {showSubmit && (
        <>
          <button
            type="button"
            aria-label="Close submit"
            onClick={() => setShowSubmit(false)}
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            data-testid="ef-submit-overlay"
          />
          <div className="fixed z-50 inset-x-2 top-4 bottom-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-12 sm:bottom-12 sm:w-[640px] overflow-y-auto">
            <SubmitForm onClose={() => setShowSubmit(false)} />
          </div>
        </>
      )}
      {showHowTo && <HowToReadModal onClose={closeHowTo} />}

      {/* DetailPanel — modal for edge selection (only) */}
      <DetailPanel
        entity={selectedEntity}
        edge={selectedEdge}
        allNodes={graph.nodes}
        onClose={closeDetail}
      />

      {/* MobileBottomSheet — mobile-native entity detail (hidden on lg+) */}
      <MobileBottomSheet
        entity={mobileSheetOpen ? inlineEntity : null}
        sources={inlineSources}
        allEdges={graph.edges}
        onClose={closeMobileSheet}
      />
    </div>
  );
};

export default EpsteinFiles;

// =============================================================================
// CONTROL ROW
// =============================================================================
const ControlRow = ({ mode, onMode, filter, onFilter, search, onSearch }) => (
  <div className="mb-4 space-y-3" data-testid="ef-control-row">
    {/* Toggle + search */}
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex border border-[#2D2A26] bg-[#0a0907]" data-testid="ef-mode-toggle">
        <button
          onClick={() => onMode("board")}
          data-testid="ef-mode-board"
          className={
            "flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] " +
            (mode === "board"
              ? "bg-[#D92D20]/15 text-[#D92D20] border-r border-[#D92D20]/40"
              : "text-[#A39E93] hover:text-[#E4DFD3] border-r border-[#2D2A26]")
          }
        >
          <LayoutGrid size={11} /> Board
        </button>
        <button
          onClick={() => onMode("list")}
          data-testid="ef-mode-list"
          className={
            "flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] " +
            (mode === "list"
              ? "bg-[#D92D20]/15 text-[#D92D20]"
              : "text-[#A39E93] hover:text-[#E4DFD3]")
          }
        >
          <List size={11} /> List
        </button>
      </div>

      <div className="relative flex-1 min-w-[200px] sm:max-w-md ml-auto">
        <Search
          size={12}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5a544a]"
        />
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          data-testid="ef-search-input"
          placeholder="Search entities, orgs, docs…"
          className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none pl-7 pr-3 py-2 font-mono text-[11px] text-[#E4DFD3] placeholder:text-[#5a544a] tracking-wide"
        />
      </div>
    </div>

    {/* Category filter chips */}
    <div className="flex flex-wrap gap-1.5" data-testid="ef-filter-chips">
      {BOARD_FILTERS.map((f) => {
        const active = filter === f.key;
        return (
          <button
            key={f.key}
            onClick={() => onFilter(f.key)}
            data-testid={`ef-filter-${f.key}`}
            className={
              "font-mono text-[9px] uppercase tracking-[0.25em] border px-2.5 py-1 transition-colors " +
              (active
                ? "border-[#D92D20] text-[#D92D20] bg-[#1a0808]"
                : "border-[#454038] text-[#A39E93] hover:border-[#D92D20] hover:text-[#E4DFD3]")
            }
          >
            {f.label}
          </button>
        );
      })}
    </div>
  </div>
);

// =============================================================================
// LEGEND
// =============================================================================
const Legend = () => (
  <div
    className="border border-[#2D2A26] bg-[#0a0907] px-3 py-2 mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5"
    data-testid="ef-legend"
  >
    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">// LEGEND</div>
    <LegendItem color="#D92D20" label="Confirmed / Source-Backed" />
    <LegendItem color="#7A8294" label="Supporting / Low Confidence" dashed />
  </div>
);
const LegendItem = ({ color, label, dashed }) => (
  <div className="flex items-center gap-1.5">
    <div
      className="w-6 h-0"
      style={{
        borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}`,
      }}
    />
    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93]">{label}</span>
  </div>
);

// =============================================================================
// TIMELINE
// =============================================================================
const TimelineView = ({ events = [], loading }) => {
  if (loading) {
    return <div className="font-mono text-xs text-[#757065]">// LOADING TIMELINE…</div>;
  }
  if (events.length === 0) {
    return (
      <div
        className="border border-dashed border-[#2D2A26] p-10 text-center font-mono text-xs uppercase tracking-widest text-[#757065]"
        data-testid="ef-timeline-empty"
      >
        No timeline events on file yet.
      </div>
    );
  }
  return (
    <div className="border-l-2 border-[#D92D20]/30 pl-4 space-y-3" data-testid="ef-timeline-view">
      {events.map((t) => {
        const cm = CONFIDENCE_META[t.confidence_level] || {};
        return (
          <div
            key={t.id}
            className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 relative"
            data-testid={`ef-timeline-${t.id}`}
          >
            <div className="absolute -left-[22px] top-3 w-3 h-3 rounded-full bg-[#D92D20]" />
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="font-mono text-[10px] text-[#D92D20] uppercase tracking-[0.3em]">
                {t.date || "—"}
              </span>
              {t.event_type && (
                <span className="font-mono text-[9px] text-[#A39E93] uppercase tracking-[0.25em]">
                  {t.event_type.replace(/_/g, " ")}
                </span>
              )}
              {t.confidence_level && (
                <span
                  style={{ color: cm.color }}
                  className="font-mono text-[9px] uppercase tracking-[0.25em] ml-auto"
                >
                  {cm.label}
                </span>
              )}
            </div>
            <div className="font-heading text-base text-[#E4DFD3]">{t.title}</div>
            {t.location_label && (
              <div className="font-mono text-[10px] text-[#757065]">@ {t.location_label}</div>
            )}
            {t.summary && <div className="text-xs text-[#A39E93] mt-1">{t.summary}</div>}
            {t.legal_note && (
              <div className="font-mono text-[9px] text-[#E0A526] mt-1 italic">⚠ {t.legal_note}</div>
            )}
            <div className="font-mono text-[10px] text-[#757065] mt-1">
              {(t.involved_entity_ids || []).length} entities ·{" "}
              {(t.evidence_ids || []).length} evidence ·{" "}
              {(t.source_refs || []).length} sources
            </div>
          </div>
        );
      })}
    </div>
  );
};

// =============================================================================
// DOCUMENTS
// =============================================================================
const DocumentsView = ({ items = [], loading }) => {
  if (loading) return <div className="font-mono text-xs text-[#757065]">// LOADING DOCUMENTS…</div>;
  if (items.length === 0) {
    return (
      <div
        className="border border-dashed border-[#2D2A26] p-10 text-center font-mono text-xs uppercase tracking-widest text-[#757065]"
        data-testid="ef-documents-empty"
      >
        No approved documents on file yet.
      </div>
    );
  }
  // Group by evidence_type
  const groups = {};
  items.forEach((i) => { (groups[i.evidence_type || "other"] ||= []).push(i); });
  const ordered = [
    ...EVIDENCE_GROUPS.filter((g) => groups[g.key]),
    ...Object.keys(groups)
      .filter((k) => !EVIDENCE_GROUPS.find((g) => g.key === k))
      .map((k) => ({ key: k, label: k.replace(/_/g, " ").toUpperCase() })),
  ];
  return (
    <div className="space-y-4" data-testid="ef-documents-view">
      {ordered.map((g) => (
        <div key={g.key} className="border border-[#2D2A26] bg-[#0F0F0F]">
          <div className="px-3 py-2 border-b border-[#2D2A26] font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] flex items-center justify-between">
            <span>{g.label}</span>
            <span className="text-[#757065]">· {groups[g.key].length}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 p-2">
            {groups[g.key].map((i) => {
              const cm = CONFIDENCE_META[i.confidence_level] || {};
              return (
                <a
                  key={i.id}
                  href={`/epstein-files/evidence/${i.slug}`}
                  data-testid={`ef-doc-${i.id}`}
                  className="border border-[#2D2A26] hover:border-[#D92D20]/70 bg-[#0a0907] p-3 group"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <FileText size={11} className="text-[#5a544a]" />
                    <span className="font-mono text-[9px] text-[#A39E93] uppercase tracking-[0.25em]">
                      {(i.evidence_type || "other").replace(/_/g, " ")}
                    </span>
                    {i.date && (
                      <span className="font-mono text-[9px] text-[#D92D20] uppercase tracking-[0.25em] ml-auto">
                        {i.date}
                      </span>
                    )}
                  </div>
                  <div className="font-heading text-sm text-[#E4DFD3] group-hover:text-white line-clamp-2">
                    {i.title}
                  </div>
                  {i.summary && (
                    <div className="text-[11px] text-[#A39E93] mt-1 line-clamp-2">{i.summary}</div>
                  )}
                  <div className="font-mono text-[9px] text-[#757065] mt-1.5 flex items-center justify-between">
                    <span>
                      {(i.linked_entity_ids || []).length} entities ·{" "}
                      {(i.source_refs || []).length} sources
                    </span>
                    {i.confidence_level && (
                      <span style={{ color: cm.color }} className="uppercase tracking-[0.25em]">
                        {cm.label}
                      </span>
                    )}
                  </div>
                  {i.legal_note && (
                    <div className="font-mono text-[9px] text-[#E0A526] mt-1 italic">⚠ {i.legal_note}</div>
                  )}
                </a>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// KEY SUBJECTS — Directory by category
// =============================================================================
const DirectoryByCategory = ({ nodes = [], edges = [], anchorId, selectedId, onSelectEntity }) => {
  const adjacency = {};
  edges.forEach((e) => {
    (adjacency[e.src_entity_id] ||= []).push(e);
    (adjacency[e.dst_entity_id] ||= []).push(e);
  });

  const groups = {};
  nodes.forEach((n) => {
    if (n.id === anchorId) { (groups["Anchor"] ||= []).push(n); return; }
    // Prefer explicit editorial label; otherwise fall back to a deterministic
    // bucket derived from the entity kind so Phase 2 mixed-node types (shell
    // companies, aircraft, flight events, legal records, documents, etc.) get
    // a real Directory home instead of falling into "Other".
    const key = n.category_label
      || n.category
      || KIND_TO_DIRECTORY_BUCKET[n.kind]
      || "Other";
    (groups[key] ||= []).push(n);
  });
  const orderedKeys = [
    ...DIRECTORY_CATEGORIES.filter((k) => groups[k]),
    ...Object.keys(groups).filter((k) => !DIRECTORY_CATEGORIES.includes(k)),
  ];

  return (
    <div className="space-y-4" data-testid="ef-directory-by-category">
      {orderedKeys.map((groupKey) => {
        const items = groups[groupKey] || [];
        return (
          <div key={groupKey} className="border border-[#2D2A26] bg-[#0F0F0F]">
            <div className="px-3 py-2 border-b border-[#2D2A26] font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
              {groupKey} <span className="text-[#757065]">· {items.length}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 p-2">
              {items.map((e) => {
                const conns = (adjacency[e.id] || []).length;
                const cm = CONFIDENCE_META[e.confidence_level] || {};
                const isSelected = e.id === selectedId;
                return (
                  <button
                    key={e.id}
                    onClick={() => onSelectEntity?.(e)}
                    data-testid={`ef-dir-card-${e.id}`}
                    className={
                      "text-left border bg-[#0a0907] px-3 py-2 " +
                      (isSelected
                        ? "border-[#E0A526] shadow-[0_0_0_2px_rgba(224,165,38,0.30)]"
                        : "border-[#2D2A26] hover:border-[#E0A526]/70")
                    }
                  >
                    <div className="flex items-start gap-2 mb-1">
                      {e.photo_url ? (
                        <img
                          src={e.photo_url}
                          alt=""
                          className="w-10 h-10 object-cover border border-[#2D2A26]"
                        />
                      ) : (
                        <div className="w-10 h-10 border border-dashed border-[#2D2A26] flex items-center justify-center text-[#454038] text-xs">
                          ▢
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-heading text-sm text-[#E4DFD3] truncate">
                          {e.name}
                        </div>
                        <div className="font-mono text-[9px] text-[#757065] uppercase tracking-[0.25em]">
                          {e.kind}
                        </div>
                      </div>
                      {e.confidence_level && (
                        <span
                          style={{ color: cm.color }}
                          className="font-mono text-[8px] uppercase tracking-[0.25em]"
                        >
                          {cm.label}
                        </span>
                      )}
                    </div>
                    {e.connection_summary_to_epstein && (
                      <div className="text-[11px] text-[#A39E93] line-clamp-2 mb-1">
                        {e.connection_summary_to_epstein}
                      </div>
                    )}
                    <div className="font-mono text-[9px] text-[#757065] mt-1">
                      {conns} conn · {(e.source_refs || []).length} sources
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
